import React, { useEffect, useState } from 'react';
import { SongAdProject, SongAdScene } from '../../services/api/songAdService';
import { TaskyButton, TaskyCard, TaskyEmptyState, TaskyAlert } from '../tasky';
import { Loader2, Download, Combine, CheckCircle2, Film, FileText, Music2, ImageIcon, Wand2, RotateCcw } from 'lucide-react';

/**
 * ステップ⑥: 書き出し（合成）。
 *
 * 楽曲＋映像（動画化シーンはクリップ、それ以外は絵コンテの静止画）＋歌詞テロップを
 * ffmpeg（または将来的にRemotion）で合成する。song_url と全シーンの絵コンテが揃うまでは合成できない。
 *
 * v3で追加（FB12）:
 * - 合成中は§22の生成中UXに沿った段階メッセージを表示する。backendのcompose jobには
 *   粒度の細かい進捗（%/step）が無いため、実データは「composing中かどうか」のポーリングのみとし、
 *   メッセージ自体はクライアント側のタイマーで演出する（見た目だけの移植。v3罠#3の指示どおり）
 * - compose_failed時は「再試行する」の文言で明示し、§23の「前工程に戻る」導線として
 *   各ステップへのショートカットを常設する
 */

interface ExportStepProps {
  project: SongAdProject;
  scenes: SongAdScene[];
  isRequesting: boolean;
  onCompose: () => Promise<void>;
  onGoToLyrics: () => void;
  onGoToSong: () => void;
  onGoToStoryboard: () => void;
  onGoToMotion: () => void;
}

/** §22の生成中UXメッセージ群のうち、最終組み立て（合成job）に相当する部分だけを使う */
export const COMPOSE_STAGE_MESSAGES = [
  '楽曲とシーンを確認しています',
  '歌詞テロップを配置しています',
  'ブランドロゴとCTAを配置しています',
  'シーンをつなぎ合わせています',
  '完成動画を組み立てています',
];

const COMPOSE_STAGE_INTERVAL_MS = 2600;

/** 合成中は段階メッセージを一定間隔で進める。実際の完了判定はproject.statusのポーリングに委ねる（演出のみ） */
export function useComposeStageMessage(active: boolean): { message: string; percent: number } {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!active) {
      setIndex(0);
      return undefined;
    }
    const timer = setInterval(() => {
      setIndex((i) => Math.min(COMPOSE_STAGE_MESSAGES.length - 1, i + 1));
    }, COMPOSE_STAGE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [active]);

  const percent = Math.round(((index + 1) / COMPOSE_STAGE_MESSAGES.length) * 92);
  return { message: COMPOSE_STAGE_MESSAGES[index], percent };
}

const ExportStep: React.FC<ExportStepProps> = ({
  project,
  scenes,
  isRequesting,
  onCompose,
  onGoToLyrics,
  onGoToSong,
  onGoToStoryboard,
  onGoToMotion,
}) => {
  const isComposing = project.status === 'composing';
  const { message: stageMessage, percent: stagePercent } = useComposeStageMessage(isComposing);

  const processNav = (
    <div className="flex flex-wrap items-center gap-1.5 mb-5">
      <span className="text-xs text-gray-400 mr-1">工程をやり直す:</span>
      <TaskyButton variant="outline" size="sm" onClick={onGoToLyrics}>
        <FileText className="w-3.5 h-3.5" />
        歌詞
      </TaskyButton>
      <TaskyButton variant="outline" size="sm" onClick={onGoToSong}>
        <Music2 className="w-3.5 h-3.5" />
        楽曲
      </TaskyButton>
      <TaskyButton variant="outline" size="sm" onClick={onGoToStoryboard}>
        <ImageIcon className="w-3.5 h-3.5" />
        ストーリーボード
      </TaskyButton>
      <TaskyButton variant="outline" size="sm" onClick={onGoToMotion}>
        <Wand2 className="w-3.5 h-3.5" />
        モーション
      </TaskyButton>
    </div>
  );

  if (scenes.length === 0) {
    return (
      <div>
        {processNav}
        <TaskyEmptyState
          icon={<Film />}
          title="先に絵コンテを作成してください"
          action={
            <TaskyButton onClick={onGoToStoryboard}>
              <ImageIcon className="w-4 h-4" />
              ストーリーボードへ
            </TaskyButton>
          }
        />
      </div>
    );
  }

  const missingStoryboards = scenes.filter((s) => !s.storyboard.image);
  const hasSong = !!project.song_url;
  const allReady = hasSong && missingStoryboards.length === 0;
  const composeFailed = project.status === 'compose_failed';

  return (
    <div className="space-y-6">
      {processNav}

      {/* シーンサムネイル一覧 */}
      <div>
        <h3 className="text-sm font-semibold text-dark mb-2">
          シーン
          <span className="ml-2 text-xs font-normal text-gray-500">{scenes.length}シーン</span>
        </h3>
        <div className="flex gap-2 overflow-x-auto pb-2">
          {scenes.map((scene, index) => (
            <div key={scene.id} className="shrink-0 w-32">
              <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden relative flex items-center justify-center border border-warm-border">
                {scene.clip.video ? (
                  <video src={scene.clip.video.url} preload="metadata" muted className="w-full h-full object-cover" />
                ) : scene.storyboard.image ? (
                  <img src={scene.storyboard.image.url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Film className="w-6 h-6 text-gray-300" />
                )}
              </div>
              <div className="flex items-center justify-between mt-1 px-0.5">
                <span className="text-[11px] text-gray-500">{index + 1}</span>
                {scene.storyboard.image ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                ) : (
                  <span className="text-[11px] text-amber-600">未生成</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 合成アクション */}
      <TaskyCard variant="warm" padding="md">
        {!hasSong && (
          <TaskyAlert tone="warning" className="mb-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <span>先に曲を生成してください。</span>
              <TaskyButton variant="outline" size="sm" onClick={onGoToSong}>
                楽曲へ
              </TaskyButton>
            </div>
          </TaskyAlert>
        )}
        {hasSong && missingStoryboards.length > 0 && (
          <TaskyAlert tone="warning" className="mb-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <span>絵コンテ未生成のシーンが{missingStoryboards.length}件あります。</span>
              <TaskyButton variant="outline" size="sm" onClick={onGoToStoryboard}>
                ストーリーボードへ
              </TaskyButton>
            </div>
          </TaskyAlert>
        )}

        {composeFailed && project.compose_error_message && (
          <TaskyAlert tone="error" className="mb-3">
            合成に失敗しました: {project.compose_error_message}
          </TaskyAlert>
        )}

        <TaskyButton onClick={onCompose} disabled={!allReady || isComposing || isRequesting} isLoading={isComposing || isRequesting}>
          {composeFailed ? <RotateCcw className="w-4 h-4" /> : <Combine className="w-4 h-4" />}
          {isComposing
            ? '合成処理中...（完了すると自動で表示されます）'
            : composeFailed
              ? '再試行する'
              : project.final_video_url
                ? '再合成する'
                : '合成する'}
        </TaskyButton>

        {isComposing && (
          <div className="mt-4">
            <div className="flex items-center gap-2 text-sm text-gray-700 mb-2">
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
              {stageMessage}
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div
                className="bg-primary h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${stagePercent}%` }}
              />
            </div>
          </div>
        )}
      </TaskyCard>

      {/* 完成動画 */}
      {project.final_video_url && (
        <div>
          <h3 className="text-sm font-semibold text-dark mb-2 flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            完成動画
            {project.composed_at && (
              <span className="text-xs font-normal text-gray-400">
                （{new Date(project.composed_at).toLocaleString('ja-JP')} 作成）
              </span>
            )}
          </h3>
          {/* 9:16 だと幅基準(max-w-2xl)のままでは高さが約1194pxになりビューポートからはみ出すため、
              高さも max-h-[70vh] で制限し、幅は縦横比なりに auto で縮める（16:9・1:1はこれまでどおり幅基準で収まる） */}
          <div className="max-w-2xl mx-auto flex flex-col items-center">
            <video
              src={project.final_video_url}
              controls
              className="max-h-[70vh] w-auto max-w-full rounded-xl border border-warm-border bg-black object-contain"
            />
            <a
              href={project.final_video_url}
              download={`${project.title}.mp4`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-3 px-4 py-2 text-sm border border-warm-border rounded-lg hover:bg-gray-100"
            >
              <Download className="w-4 h-4" />
              ダウンロード
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportStep;
